/**
 * ParsedDocument Mapper
 *
 * Utilities for mapping AI-extracted document data to expense form fields.
 * These functions are pure and testable, following DRY principles.
 */

import { format } from 'date-fns';
import type { ParsedDocument, LineItem } from '@/types/parsedDocument';
import { PAYMENT_TERMS_OPTIONS } from '@/lib/paymentTerms';
import type { TaxRateType } from '@/lib/taxCalculations';
import { scannedVatBase } from '@/lib/taxRateScan';

/**
 * Expense line item structure expected by the form
 */
export interface ExpenseLineItem {
  expense_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: TaxRateType;
  tax_inclusive: boolean;
  is_reverse_charge: boolean;
  exemption_reason?: string;
  exemption_reason_other?: string;
  /**
   * Placeholder marker: every scanned line starts at 0 awaiting the
   * line-items grid's fill pass (InvoiceLineItems), which picks the ONE rate
   * at `tax_rate_hint`, or the org default when there is no hint.
   * Form-only, never sent.
   */
  tax_rate_unset?: boolean;
  /**
   * The percentage the document printed for the line (or, with
   * `tax_rate_hint_computed`, that its totals imply). Absent when the scan
   * found none. Never snapped to a near rate: no unique exact match leaves
   * the line at 0% with "Scanned: N%" shown. Form-only, never sent.
   */
  tax_rate_hint?: number;
  /** The hint is tax ÷ subtotal (rounding noise) — matched within ±0.01. */
  tax_rate_hint_computed?: boolean;
  /**
   * The VAT AMOUNT the document printed for this line (a line's own tax, or
   * the document's tax on the totals fallback line), when it printed one.
   * Used only to gross the line up exactly when the user picks "VAT not
   * reclaimable" (lib/taxRateScan.ts). Form-only, never sent.
   */
  tax_rate_hint_vat?: number;
  /** The net line total that VAT was printed on; a later quantity/price edit makes it stale. Form-only. */
  tax_rate_hint_vat_base?: number;
  /**
   * Set by the "VAT not reclaimable" gross-up (lib/taxRateScan.ts): the net
   * unit price, the price written, and "the user stated the amount". Form-only.
   */
  tax_rate_hint_net?: number;
  tax_rate_hint_gross?: number;
  tax_rate_hint_amount_kept?: boolean;
}

/**
 * Form data structure returned by the expense mapper
 */
export interface ParsedDocumentFormData {
  business_name?: string;
  expense_date?: string;
  reference?: string;
  memo?: string;
  /** Normalized (uppercase) currency code the document is denominated in, when detected */
  currency?: string;
  line_items: ExpenseLineItem[];
}

/**
 * Form data structure returned by the bill (AP invoice) mapper
 */
export interface ParsedDocumentBillFormData {
  business_name?: string;
  invoice_number?: string;
  invoice_date?: string;
  payment_terms?: string;
  memo?: string;
  /** Normalized (uppercase) currency code the document is denominated in, when detected */
  currency?: string;
  line_items: ExpenseLineItem[];
}

/**
 * Default line item when no data is available
 */
const DEFAULT_LINE_ITEM: ExpenseLineItem = {
  expense_account_id: '',
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: 0,
  tax_inclusive: false,
  is_reverse_charge: false,
  exemption_reason: '',
  exemption_reason_other: '',
  tax_rate_unset: true,
};

/**
 * Get the vendor/merchant name from a parsed document
 */
function getBusinessName(doc: ParsedDocument): string | undefined {
  return doc.vendor?.name || doc.merchant?.name;
}

/**
 * Normalize an extracted currency code (e.g. " eur " → "EUR").
 * Returns undefined for missing/blank values.
 */
function normalizeDocumentCurrency(currency: string | undefined): string | undefined {
  const normalized = currency?.trim().toUpperCase();
  return normalized || undefined;
}

/**
 * Get the transaction/invoice date formatted for the expense form's date
 * input (YYYY-MM-DD). `expenses.expense_date` is a plain `date` column, so
 * the scanned document contributes a calendar day, never an instant — mirrors
 * `getInvoiceDate` below.
 */
function getExpenseDate(doc: ParsedDocument): string | undefined {
  const dateStr = doc.invoiceDate || doc.transactionDate;
  if (!dateStr) return undefined;

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
    return format(date, 'yyyy-MM-dd');
  } catch {
    return undefined;
  }
}

/**
 * The tax fields of a scanned line: always a 0% placeholder (the scanned
 * percentage is a hint, never a rate — the line must name a rate the org
 * has, which only the fill pass can resolve), with the hint when one was
 * found. The parser coerces a real 0 to undefined, so 0 means "no rate
 * found": no hint, and the fill pass applies the org default.
 */
function scannedTaxFields(
  scannedRate: number,
  computed: boolean,
  scannedVat: number | undefined,
  amounts: Pick<ExpenseLineItem, 'quantity' | 'unit_price'>,
): Pick<ExpenseLineItem, 'tax_rate' | 'tax_rate_unset' | 'tax_rate_hint' | 'tax_rate_hint_computed' | 'tax_rate_hint_vat' | 'tax_rate_hint_vat_base'> {
  return {
    tax_rate: 0,
    tax_rate_unset: true,
    ...(scannedRate > 0 && {
      tax_rate_hint: scannedRate,
      ...(computed && { tax_rate_hint_computed: true }),
      // The printed VAT amount, when there is one: the exact figure for a
      // "VAT not reclaimable" gross-up (reproduces the receipt total).
      // Stamped with the net total it belongs to, so an edited line falls
      // back to the rate.
      ...((scannedVat ?? 0) > 0 && {
        tax_rate_hint_vat: scannedVat,
        tax_rate_hint_vat_base: scannedVatBase(amounts.quantity, amounts.unit_price),
      }),
    }),
  };
}

/**
 * Map a single parsed line item to expense line item format
 */
function mapLineItem(item: LineItem): ExpenseLineItem {
  const amounts = { quantity: item.quantity ?? 1, unit_price: item.unitPrice ?? item.amount ?? 0 };
  return {
    expense_account_id: '', // User must select
    description: item.description || '',
    ...amounts,
    tax_inclusive: false,
    is_reverse_charge: false,
    exemption_reason: '',
    exemption_reason_other: '',
    // A printed rate: matched exactly.
    ...scannedTaxFields(item.taxRate ?? 0, false, item.tax, amounts),
  };
}

/**
 * Calculate tax rate from tax and subtotal amounts
 */
function calculateTaxRate(tax?: number, subtotal?: number): number {
  if (!tax || !subtotal || subtotal === 0) return 0;
  return Math.round((tax / subtotal) * 100 * 100) / 100; // Round to 2 decimal places
}

/**
 * Create a fallback line item from document totals when no line items exist
 */
function createFallbackLineItem(doc: ParsedDocument): ExpenseLineItem {
  const businessName = getBusinessName(doc);
  const taxRate = calculateTaxRate(doc.tax, doc.subtotal);
  const amounts = { quantity: 1, unit_price: doc.subtotal ?? doc.total ?? 0 };

  return {
    expense_account_id: '',
    description: businessName || 'Expense',
    ...amounts,
    tax_inclusive: false,
    is_reverse_charge: false,
    exemption_reason: '',
    exemption_reason_other: '',
    // Computed from totals (tax ÷ subtotal): matched within ±0.01. No tax on
    // the document → no hint → the org default.
    ...scannedTaxFields(taxRate, true, doc.tax, amounts),
  };
}

/**
 * Map a ParsedDocument to expense form data structure
 *
 * @param parsedData - The AI-extracted document data
 * @returns Form data structure or null if input is invalid
 */
export function mapParsedDocumentToExpenseForm(
  parsedData: ParsedDocument | null | undefined
): ParsedDocumentFormData | null {
  if (!parsedData) return null;

  const businessName = getBusinessName(parsedData);
  const expenseDate = getExpenseDate(parsedData);

  // Map line items or create fallback
  let lineItems: ExpenseLineItem[];
  if (parsedData.items && parsedData.items.length > 0) {
    lineItems = parsedData.items.map(mapLineItem);
  } else {
    // Create single line item from totals
    lineItems = [createFallbackLineItem(parsedData)];
  }

  return {
    business_name: businessName,
    expense_date: expenseDate,
    reference: parsedData.invoiceNumber,
    memo: undefined, // Optional: could add context here
    currency: normalizeDocumentCurrency(parsedData.currency),
    line_items: lineItems,
  };
}

/**
 * Apply an exchange rate to mapped line items, converting unit prices into the
 * target currency (rounded to 2 decimals). Quantities and tax rates (percentages)
 * are unchanged. Per-line rounding means the summed converted total can drift a
 * cent from rate × document total — acceptable, the result is an estimate.
 */
export function convertLineItemAmounts(
  items: ExpenseLineItem[],
  rate: number
): ExpenseLineItem[] {
  return items.map((item) => {
    const unit_price = Math.round(item.unit_price * rate * 100) / 100;
    return {
      ...item,
      unit_price,
      // The printed VAT amount converts with the prices it belongs to, and is
      // re-stamped with the converted net total.
      ...(item.tax_rate_hint_vat !== undefined && {
        tax_rate_hint_vat: Math.round(item.tax_rate_hint_vat * rate * 100) / 100,
        tax_rate_hint_vat_base: scannedVatBase(item.quantity, unit_price),
      }),
    };
  });
}

/**
 * Build the audit-trail memo note recorded when a scanned document's amounts
 * were converted into the org currency. Deliberately locale-NEUTRAL: the memo
 * is persisted to the transaction description, so a translated string would
 * freeze the creator's UI language into the books.
 *
 * Example: "Original: 329.00 EUR @ 1.083 (2026-07-01, ECB est.)"
 */
export function buildConversionMemoNote(
  originalTotal: number | undefined,
  docCurrency: string,
  rate: number,
  rateDate: string | undefined
): string {
  const rateStr = String(Number(rate.toPrecision(6)));
  const dateSuffix = rateDate ? `${rateDate}, ECB est.` : 'ECB est.';
  const prefix =
    originalTotal !== undefined
      ? `Original: ${originalTotal.toFixed(2)} ${docCurrency}`
      : `Converted from ${docCurrency}`;
  return `${prefix} @ ${rateStr} (${dateSuffix})`;
}

/**
 * Normalize a free-text payment term (e.g. "Net 30") to a dropdown option value (e.g. "net_30")
 */
function normalizePaymentTerm(term: string | undefined): string | undefined {
  if (!term) return undefined;
  const normalized = term.toLowerCase().replace(/\s+/g, '_');
  const match = PAYMENT_TERMS_OPTIONS.find(
    opt => opt.value === normalized || opt.label.toLowerCase() === term.toLowerCase()
  );
  return match?.value;
}

/**
 * Get the invoice date formatted for date input (YYYY-MM-DD)
 */
function getInvoiceDate(doc: ParsedDocument): string | undefined {
  const dateStr = doc.invoiceDate || doc.transactionDate;
  if (!dateStr) return undefined;

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
    return format(date, 'yyyy-MM-dd');
  } catch {
    return undefined;
  }
}

/**
 * Map a ParsedDocument to bill (AP invoice) form data structure
 *
 * Key differences from expense mapper:
 * - Returns invoice_number, invoice_date as separate fields
 * - Does NOT derive due_date (form handles that via payment terms logic)
 * - Uses date-only format (YYYY-MM-DD) instead of datetime-local
 *
 * @param parsedData - The AI-extracted document data
 * @returns Form data structure or null if input is invalid
 */
export function mapParsedDocumentToBillForm(
  parsedData: ParsedDocument | null | undefined
): ParsedDocumentBillFormData | null {
  if (!parsedData) return null;

  const businessName = getBusinessName(parsedData);
  const invoiceDate = getInvoiceDate(parsedData);

  // Map line items or create fallback
  let lineItems: ExpenseLineItem[];
  if (parsedData.items && parsedData.items.length > 0) {
    lineItems = parsedData.items.map(mapLineItem);
  } else {
    lineItems = [createFallbackLineItem(parsedData)];
  }

  return {
    business_name: businessName,
    invoice_number: parsedData.invoiceNumber,
    invoice_date: invoiceDate,
    payment_terms: normalizePaymentTerm(parsedData.paymentTerm),
    memo: undefined,
    currency: normalizeDocumentCurrency(parsedData.currency),
    line_items: lineItems,
  };
}

/**
 * Merge multiple parsed documents into a single document
 * Used when an inbox message has multiple attachments with parsed data
 *
 * @param parsedDocs - Array of parsed documents (may contain null/undefined)
 * @returns Merged document or null if no valid documents
 */
export function mergeParsedDocuments(
  parsedDocs: (ParsedDocument | null | undefined)[]
): ParsedDocument | null {
  // Filter out null/undefined
  const validDocs = parsedDocs.filter((doc): doc is ParsedDocument => doc != null);

  if (validDocs.length === 0) return null;
  if (validDocs.length === 1) return validDocs[0];

  // Start with a copy of the first document
  const merged: ParsedDocument = { ...validDocs[0] };

  // Collect all line items from all documents
  const allItems: LineItem[] = [];
  let totalSum = 0;
  let subtotalSum = 0;
  let taxSum = 0;

  for (const doc of validDocs) {
    // Add items
    if (doc.items) {
      allItems.push(...doc.items);
    }

    // Sum totals for context
    if (doc.total) totalSum += doc.total;
    if (doc.subtotal) subtotalSum += doc.subtotal;
    if (doc.tax) taxSum += doc.tax;

    // Use first non-null vendor/merchant name
    if (!merged.vendor?.name && !merged.merchant?.name) {
      if (doc.vendor?.name) merged.vendor = doc.vendor;
      if (doc.merchant?.name) merged.merchant = doc.merchant;
    }

    // Use earliest date
    const docDate = doc.invoiceDate || doc.transactionDate;
    const mergedDate = merged.invoiceDate || merged.transactionDate;
    if (docDate && (!mergedDate || docDate < mergedDate)) {
      if (doc.documentType === 'invoice') {
        merged.invoiceDate = docDate;
      } else {
        merged.transactionDate = docDate;
      }
    }

    // Use first invoice number found
    if (!merged.invoiceNumber && doc.invoiceNumber) {
      merged.invoiceNumber = doc.invoiceNumber;
    }
  }

  // Set merged items and totals
  merged.items = allItems.length > 0 ? allItems : undefined;
  merged.total = totalSum > 0 ? totalSum : merged.total;
  merged.subtotal = subtotalSum > 0 ? subtotalSum : merged.subtotal;
  merged.tax = taxSum > 0 ? taxSum : merged.tax;

  // Currency survives the merge only when EVERY document carries the same
  // known currency. A doc with no detected currency alongside a EUR doc counts
  // as mixed — it could be anything, and the summed totals above would be
  // meaningless in any single currency. Mixed sets get no currency (and thus
  // no downstream conversion); the amounts remain raw sums.
  const currencies = validDocs.map(doc => normalizeDocumentCurrency(doc.currency));
  const allSameKnownCurrency =
    currencies[0] !== undefined && currencies.every(c => c === currencies[0]);
  if (allSameKnownCurrency) {
    merged.currency = currencies[0];
  } else {
    // {...validDocs[0]} copied the first doc's currency — remove it.
    delete merged.currency;
    if (process.env.NODE_ENV !== 'production' && currencies.some(c => c !== undefined)) {
      console.warn(
        '[mergeParsedDocuments] Attachments span multiple/unknown currencies; ' +
          'merged totals are raw sums and no currency conversion will be applied.',
      );
    }
  }

  return merged;
}
